import { useEffect } from 'react';

type ModKey = 'ctrl' | 'shift' | 'alt' | 'meta';

type Hotkey = ReadonlyArray<string>;
type HotkeyInput = Hotkey | ReadonlyArray<Hotkey>;

type NormalizedHotkey = {
	mainKey: string | null;
	modifiers: Record<ModKey, boolean>;
};

type UseHotkeyOptions = {
	eventType?: 'keydown' | 'keyup';
	ignoreInput?: boolean;
	preventDefault?: boolean;
};

type HotkeyHandler = {
	hotkey: HotkeyInput;
	callback: () => void;
};

const MODIFIER_ALIASES: Record<string, ModKey> = {
	ctrl: 'ctrl',
	control: 'ctrl',
	shift: 'shift',
	alt: 'alt',
	option: 'alt',
	meta: 'meta',
	cmd: 'meta',
	command: 'meta',
};

const isMacOS = () =>
	typeof navigator !== 'undefined' &&
	/Mac|iPod|iPhone|iPad/.test(navigator.userAgent || navigator.platform || '');

const isEditableTarget = (target: EventTarget | null) => {
	const el = target as HTMLElement | null;
	if (!el) return false;

	const tag = el.tagName;
	return el.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
};

const normalizeHotkey = (hotkey: Hotkey): NormalizedHotkey => {
	const modifiers: Record<ModKey, boolean> = { ctrl: false, shift: false, alt: false, meta: false };
	let mainKey: string | null = null;

	hotkey.forEach((key) => {
		const normalizedKey = key.trim().toLowerCase();
		if (!normalizedKey) return;

		const modifier = MODIFIER_ALIASES[normalizedKey];
		if (modifier) {
			modifiers[modifier] = true;
			return;
		}

		if (!mainKey) {
			mainKey = normalizedKey;
		}
	});

	return { mainKey, modifiers };
};

const isExactMatch = (event: KeyboardEvent, normalized: NormalizedHotkey) => {
	const { mainKey, modifiers } = normalized;
	if (!mainKey) return false;

	const ctrlPressed = event.ctrlKey;
	const metaPressed = event.metaKey;
	// На macOS разрешаем Command как замену Control для комбинаций с ctrl.
	const metaUsedAsCtrl = isMacOS() && !ctrlPressed && metaPressed && modifiers.ctrl;

	const ctrlMatch = modifiers.ctrl
		? ctrlPressed || metaUsedAsCtrl
		: !ctrlPressed && !metaUsedAsCtrl;
	const metaMatch = (metaUsedAsCtrl ? false : metaPressed) === modifiers.meta;
	const shiftMatch = event.shiftKey === modifiers.shift;
	const altMatch = event.altKey === modifiers.alt;

	// запрещаем реагировать на комбинации с дополнительными модификаторами
	if (!ctrlMatch || !metaMatch || !shiftMatch || !altMatch) return false;

	const eventKey = event.key?.toLowerCase();

	return eventKey === mainKey;
};

const normalizeHotkeyInput = (hotkey: HotkeyInput): ReadonlyArray<Hotkey> => {
	const first = (hotkey as unknown[])[0];

	return Array.isArray(first) ? (hotkey as ReadonlyArray<Hotkey>) : [hotkey as Hotkey];
};

/**
 * Враппер над useHotkeys для регистрации одной комбинации.
 */
export const useHotkey = (
	hotkey: HotkeyInput,
	callback: () => void,
	options: UseHotkeyOptions = {}
) => useHotkeys([{ hotkey, callback }], options);

/**
 * Универсальный хук для привязки глобальных горячих клавиш к коллбекам.
 * Игнорирует ввод в инпутах/редакторах и по умолчанию предотвращает стандартное действие браузера.
 */
export const useHotkeys = (
	handlers: ReadonlyArray<HotkeyHandler>,
	options: UseHotkeyOptions = {}
) => {
	const { eventType = 'keydown', ignoreInput = true, preventDefault = true } = options;

	useEffect(() => {
		const normalizedHandlers = handlers
			.flatMap(({ hotkey, callback }) =>
				normalizeHotkeyInput(hotkey).map((combo) => ({
					normalized: normalizeHotkey(combo),
					callback,
				}))
			)
			.filter(({ normalized }) => Boolean(normalized.mainKey));

		if (normalizedHandlers.length === 0) return;

		const handleKey = (event: KeyboardEvent) => {
			if (ignoreInput && isEditableTarget(event.target)) return;

			const matched = normalizedHandlers.find(({ normalized }) => isExactMatch(event, normalized));

			if (!matched) return;

			if (preventDefault) event.preventDefault();
			matched.callback();
		};

		window.addEventListener(eventType, handleKey);

		return () => window.removeEventListener(eventType, handleKey);
	}, [eventType, handlers, ignoreInput, preventDefault]);
};
